import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import MetricTable from "../MetricTable";
import { MetricGroup, useMetricsWithCompare } from "../useMetrics";
import { ConsultantOption, TabProps } from "../types";
import ConsultantDetail from "./ConsultantDetail";

interface ConsultantsTabProps extends TabProps {
  consultants: ConsultantOption[];
  /** Clears / sets the consultant URL param — a row click drills into one. */
  setConsultant: (id: string) => void;
  currentUser: any;
  /** Compare-to-previous-period toggle from the filter bar. */
  compare?: boolean;
  previousRange?: { start: Dayjs | null; end: Dayjs | null } | null;
  previousLabel?: string;
}

/**
 * The consultants x metrics table, replacing Program Statistics' wall of
 * charts. Selecting a consultant (the URL `consultant` param) swaps in their
 * detail view.
 */
const ConsultantsTab: React.FC<ConsultantsTabProps> = ({
  start,
  end,
  consultant,
  onDrill,
  consultants,
  setConsultant,
  currentUser,
  compare = false,
  previousRange = null,
  previousLabel,
}) => {
  const enabled = !consultant;
  const { current, previous } = useMetricsWithCompare(
    { start, end, groupBy: "consultant", enabled },
    compare ? previousRange : null,
  );
  // Consultant columns ranked by Active Clients, busiest first; ties fall
  // back to name order so the ranking stays stable.
  const ranked = useMemo(() => {
    if (!current.data) return current.data;
    const activeClients = (g: MetricGroup) => {
      const v = g.metrics?.activeClients;
      return typeof v === "number" && !Number.isNaN(v) ? v : 0;
    };
    const groups = [...current.data.groups].sort(
      (a, b) =>
        activeClients(b) - activeClients(a) || a.label.localeCompare(b.label),
    );
    return {
      ...current.data,
      catalog: current.data.catalog
        .filter(
          (metric) =>
            metric.id !== "initialContacts" && metric.id !== "b2b15min",
        )
        .map((metric) =>
          metric.id === "resourceReferrals"
            ? { ...metric, label: "Resource Partner Referral" }
            : metric,
        ),
      groups,
    };
  }, [current.data]);

  if (consultant) {
    const selected =
      consultants.find((c) => String(c._id) === String(consultant)) ||
      (String(currentUser?._id) === String(consultant)
        ? {
            _id: String(currentUser?._id),
            name: `${currentUser?.firstName || ""} ${
              currentUser?.lastName || ""
            }`.trim(),
            firstName: currentUser?.firstName || "",
            lastName: currentUser?.lastName || "",
          }
        : undefined);
    return (
      <ConsultantDetail
        consultantId={consultant}
        consultantName={selected?.name || "Consultant"}
        consultantUser={selected}
        start={start}
        end={end}
        onDrill={onDrill}
        onBack={() => setConsultant("")}
      />
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c2c34", mb: 0.5 }}>
        Consultants
      </Typography>
      <Typography variant="body2" sx={{ color: "#8a8a93", mb: 2 }}>
        Every metric by consultant. Click a name for their detail view, or any
        number to see the records behind it.
      </Typography>
      <MetricTable
        data={ranked}
        loading={current.loading}
        error={current.error}
        previous={compare ? previous.data : null}
        previousLabel={previousLabel}
        exportName="consultant-metrics"
        onGroupClick={(group) => setConsultant(group.key)}
        onCellClick={(row, group) =>
          onDrill({
            id: row.id,
            label: row.label,
            consultant: group ? group.key : undefined,
            consultantLabel: group ? group.label : undefined,
          })
        }
      />
    </Box>
  );
};

export default ConsultantsTab;
